/**
 * Tradução pt-BR dos códigos de erro mais comuns retornados pela Meta/360dialog
 * ao enviar mensagens via WhatsApp Business API.
 *
 * Ref.: https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/
 */
const META_ERROR_LABELS: Record<string, string> = {
  // Meta / 360dialog
  '131031': 'Conta Business bloqueada pela Meta',
  '131042': 'Cobrança pendente na Meta — o envio volta sozinho assim que o pagamento for confirmado',
  '131026': 'Número não recebe WhatsApp ou bloqueou',
  '131049': 'Bloqueado pela Meta (baixo engajamento)',
  '131047': 'Janela de 24h expirada — reenvie um template',
  '131053': 'Erro de upload de mídia',
  '132000': 'Template inválido',
  '132001': 'Template/idioma inválido',
  '132005': 'Texto do template muito longo',
  '132007': 'Variáveis do template inválidas',
  '132012': 'Parâmetros do template incompatíveis',
  '133000': 'Falha ao registrar o número',
  '368': 'Conta temporariamente bloqueada por violação de políticas',
  // Evolution API (HTTP status)
  '400': 'Número inválido ou formato incorreto',
  '401': 'Conexão WhatsApp desautorizada — reconecte o QR',
  '403': 'Acesso negado pelo servidor WhatsApp',
  '404': 'Instância WhatsApp não encontrada',
  '429': 'Limite temporário atingido — aguarde alguns minutos',
  '500': 'Erro interno do servidor WhatsApp',
  '502': 'Servidor WhatsApp indisponível',
  '503': 'Servidor WhatsApp temporariamente fora',
  // Genéricos
  'EDGE_ERROR': 'Erro interno ao enviar (edge function)',
  'UNSUPPORTED_FORMAT': 'Formato de arquivo não suportado',
};

/**
 * Detecta valores corrompidos que NUNCA devem aparecer pro usuário —
 * geralmente lixo de serialização que vazou pro banco.
 */
function isLixoSerializacao(s: string | null | undefined): boolean {
  if (!s) return true;
  const t = s.trim().toLowerCase();
  return (
    t === '[object object]' ||
    t === 'undefined' ||
    t === 'null' ||
    t === '' ||
    t === '{}'
  );
}

/**
 * O Bee360 grava `erro_codigo = 'bee360'` e joga o código real da Meta no
 * começo do `erro_detalhe` ("131042: Business eligibility payment issue | ...").
 * Sem isto o texto cru em inglês, com URL e tudo, vazava pra bolha da conversa.
 */
function extrairCodigoMeta(detalhe: string | null | undefined): string | null {
  if (!detalhe) return null;
  const m = /^\s*(\d{3,6})\s*:/.exec(detalhe);
  return m ? m[1] : null;
}

/**
 * Retorna uma mensagem amigável para um código de erro do provedor.
 * Se o código não for conhecido, devolve `fallback` (geralmente o `erro_detalhe`
 * cru gravado no banco) ou, em último caso, o próprio código.
 */
export function describeWhatsAppError(
  code: string | null | undefined,
  fallback?: string | null
): string {
  // Código da Meta embutido no detalhe (caso Bee360) tem precedência sobre o
  // texto cru: é ele que garante mensagem em pt-BR na tela.
  const codigoEmbutido = extrairCodigoMeta(fallback);
  if (codigoEmbutido && META_ERROR_LABELS[codigoEmbutido]) {
    return META_ERROR_LABELS[codigoEmbutido];
  }
  // Se o fallback é texto útil (não lixo de serialização), prioriza ele —
  // a edge function nova já traduz e grava texto legível no erro_detalhe.
  if (!isLixoSerializacao(fallback)) {
    return fallback!;
  }
  // Senão, usa a label do código (Meta ou Evolution HTTP)
  if (code && META_ERROR_LABELS[code]) return META_ERROR_LABELS[code];
  if (code) return `Falha no envio (HTTP ${code})`;
  return 'Falha desconhecida — tente novamente';
}

/**
 * Código a exibir ao lado do motivo. Prefere o código real da Meta embutido no
 * detalhe ao rótulo genérico do provedor ("bee360"), que não diz nada a ninguém.
 */
export function codigoExibidoWhatsApp(
  code: string | null | undefined,
  detalhe?: string | null
): string | null {
  return extrairCodigoMeta(detalhe) ?? (code || null);
}

/**
 * Mensagem pronta pra toast: "<motivo> (<código>)".
 */
export function whatsAppErrorToToast(
  code: string | null | undefined,
  fallback?: string | null
): string {
  const label = describeWhatsAppError(code, fallback);
  return code ? `${label} (${code})` : label;
}

// ─────────────────────────────────────────────────────────────────────────────
// Classificação por escopo do erro
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Códigos que afetam a CONEXÃO/WABA inteira — quando aparecem, toda
 * conversa daquela connection_id vai falhar até o problema ser resolvido.
 * Diferente dos códigos "por mensagem" (132xxx — template inválido) ou
 * "por destinatário" (131049 — marketing cap pra um usuário).
 *
 * Ref. Meta: https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/
 */
export const CONNECTION_LEVEL_ERROR_CODES = new Set<string>([
  '131031', // Business Account locked (mais comum — banimento severo)
  '368',    // Temporarily blocked for policies violations
  '133000', // Incomplete deregistration
]);

/** Códigos por destinatário — Meta capa marketing pra UM usuário, não a conta. */
export const PER_RECIPIENT_ERROR_CODES = new Set<string>([
  '131049', // Per-user marketing limit hit
]);

export function isConnectionLevelError(code: string | null | undefined): boolean {
  if (!code) return false;
  return CONNECTION_LEVEL_ERROR_CODES.has(String(code));
}

/**
 * Severidade do bloqueio — heurística usando código + frequência de falhas.
 * Sem webhook account-level (DISABLED_UPDATE) ainda, é o melhor que dá pra
 * inferir só pelos códigos de erro dos sends.
 *
 * - `ok`              : conexão saudável
 * - `restricted_temp` : bloqueio temporário (368 / poucas falhas) — provável volta sozinho
 * - `likely_banned`   : bloqueio severo (131031 contínuo) — alta chance de banimento, abrir chamado
 */
export type ConnectionSeverity = 'ok' | 'restricted_temp' | 'likely_banned';

export function classifyConnectionFailure(
  code: string | null | undefined,
  failuresInWindow: number,
  windowHours: number,
): ConnectionSeverity {
  if (!isConnectionLevelError(code)) return 'ok';
  // 368 é por natureza temporário (Meta documenta como "temporarily blocked")
  if (code === '368') return 'restricted_temp';
  // 131031 / 133000 — quanto mais frequente, mais grave
  // ≥5 falhas em ≤1h ou ≥3 em qualquer janela = banimento provável
  if (failuresInWindow >= 5 && windowHours <= 1) return 'likely_banned';
  if (failuresInWindow >= 3) return 'likely_banned';
  return 'restricted_temp';
}

/** Label curto e neutro pra UI. Sem caps lock, sem emoji chamativo. */
export function severityLabel(sev: ConnectionSeverity): string {
  if (sev === 'likely_banned') return 'Bloqueio severo';
  if (sev === 'restricted_temp') return 'Bloqueio temporário';
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatação de telefone — pra exibir no chip/popover e no texto do chamado
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formata número da conexão em padrão brasileiro internacional.
 * Aceita "5555991685872" → "+55 55 99168-5872"
 * Tolerante: se vier inesperado, retorna o input original.
 */
export function formatConnectionPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  // 13 dígitos: 55 + DDD(2) + 9 + 8 dígitos
  if (digits.length === 13 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const p1 = digits.slice(4, 9);
    const p2 = digits.slice(9, 13);
    return `+55 ${ddd} ${p1}-${p2}`;
  }
  // 12 dígitos: 55 + DDD(2) + 8 dígitos (sem o 9)
  if (digits.length === 12 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const p1 = digits.slice(4, 8);
    const p2 = digits.slice(8, 12);
    return `+55 ${ddd} ${p1}-${p2}`;
  }
  return raw;
}

// ─────────────────────────────────────────────────────────────────────────────
// Texto pronto pra colar em chamado da 360dialog
// ─────────────────────────────────────────────────────────────────────────────

export function buildIncidentText(opts: {
  connectionNome: string | null | undefined;
  connectionTelefone: string | null | undefined;
  code: string | null | undefined;
  detalhe?: string | null;
  conversationId?: string | null;
  primeiraFalhaEm?: string | null;
  totalFalhas?: number;
  windowHours?: number;
}): string {
  const nome = opts.connectionNome || 'Conexão sem nome';
  const tel = formatConnectionPhone(opts.connectionTelefone);
  const erroTexto = describeWhatsAppError(opts.code, opts.detalhe);
  const primeiraFalha = opts.primeiraFalhaEm
    ? new Date(opts.primeiraFalhaEm).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null;

  const partes: string[] = [];
  partes.push(`Conexão "${nome}"${tel ? ` (${tel})` : ''} bloqueada pela Meta.`);
  if (opts.code) {
    partes.push(`Erro ${opts.code}: ${erroTexto}.`);
  }
  if (primeiraFalha) {
    partes.push(`Primeira falha: ${primeiraFalha}.`);
  }
  if (opts.totalFalhas != null && opts.windowHours != null) {
    partes.push(`Total: ${opts.totalFalhas} falhas nas últimas ${opts.windowHours}h.`);
  }
  if (opts.conversationId) {
    partes.push(`ConversationId: ${opts.conversationId}.`);
  }
  return partes.join(' ');
}